import { useMemo, useCallback, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import Tooltip from '@components/tooltip/tooltip';
import { ContactTypes, RequestType, RequestTypeStringIds } from '@src/utils/enums';
import { Alert, Button, Icon } from '@trussworks/react-uswds';
import Checkbox from '@components/checkbox/Checkbox';
import { useUserProfile } from '@hooks';

import {
  jurisdictionalDeterminationTooltipContent,
  wotusDelineationReportTooltipContent,
  noPermitRequiredTooltipContent,
  permitTooltipContent,
  preAppTooltipContent,
  nonPcnTooltipContent,
} from '@pages/Resources/components/_shared/helper';
import _intersect from 'lodash/intersection';
import './requestTypes.scss';

const PermitApplicationDisabledRequestTypes = [
  RequestTypeStringIds.ActivityBasedNoPermitRequired,
  RequestTypeStringIds.AquaticResourceDelineationReport,
  RequestTypeStringIds.JurisdictionalDetermination,
  RequestTypeStringIds.NoPermitRequired,
  RequestTypeStringIds.NonPreconstructionNotification,
  RequestTypeStringIds.PreApplication,
  RequestTypeStringIds.PreconstructionNotification,
];
const JdDisabledRequestTypes = [
  RequestTypeStringIds.ActivityBasedNoPermitRequired,
  RequestTypeStringIds.ApplicationForDAPermit,
  RequestTypeStringIds.NoPermitRequired,
  RequestTypeStringIds.NonPreconstructionNotification,
  RequestTypeStringIds.PermitApplication,
  RequestTypeStringIds.PreApplication,
  RequestTypeStringIds.PreconstructionNotification,
];
const PreAppDisabledRequestTypes = [
  RequestTypeStringIds.ActivityBasedNoPermitRequired,
  RequestTypeStringIds.ApplicationForDAPermit,
  RequestTypeStringIds.AquaticResourceDelineationReport,
  RequestTypeStringIds.JurisdictionalDetermination,
  RequestTypeStringIds.NoPermitRequired,
  RequestTypeStringIds.NonPreconstructionNotification,
  RequestTypeStringIds.PermitApplication,
  RequestTypeStringIds.PreconstructionNotification,
];
const NprDisabledRequestTypes = [
  RequestTypeStringIds.ApplicationForDAPermit,
  RequestTypeStringIds.AquaticResourceDelineationReport,
  RequestTypeStringIds.JurisdictionalDetermination,
  RequestTypeStringIds.NonPreconstructionNotification,
  RequestTypeStringIds.PermitApplication,
  RequestTypeStringIds.PreApplication,
  RequestTypeStringIds.PreconstructionNotification,
];

const NonPcnDisabledRequestTypes = [
  RequestTypeStringIds.ActivityBasedNoPermitRequired,
  RequestTypeStringIds.ApplicationForDAPermit,
  RequestTypeStringIds.AquaticResourceDelineationReport,
  RequestTypeStringIds.JurisdictionalDetermination,
  RequestTypeStringIds.NoPermitRequired,
  RequestTypeStringIds.PermitApplication,
  RequestTypeStringIds.PreApplication,
  RequestTypeStringIds.PreconstructionNotification,
];

const unavailableRequestTypeContent = {
  [RequestType.NonPreconstructionNotification]: `The "No Pre-Construction Notification Required Screening Tool" is not available for your project due to the local District's Nationwide Permit Regional Conditions. You will need to submit a Pre-Construction Notification for your project by using the Application for a Department of the Army (DA) Permit module.`,
};

const DisabledRequestTypeNote = ({ isDisabled, requestTypeName, expandedKey, setExpandedKey }) => {
  if (!isDisabled) return null;
  const isExpanded = expandedKey === requestTypeName;
  const contentId = `request-type-note-${requestTypeName}`;

  return (
    <div className='request-type-disabled-note'>
      <div className='request-type-disabled-note__row'>
        <Icon.Warning size={3} aria-hidden='true' className='request-type-disabled-note__icon' />
        <div>
          <div className='request-type-disabled-note__text'>Option not available in your project area</div>
          <Button
            type='button'
            unstyled
            className='request-type-disabled-note__link'
            aria-expanded={isExpanded}
            aria-controls={contentId}
            onClick={() => setExpandedKey(isExpanded ? null : requestTypeName)}
          >
            Learn why this option is unavailable
          </Button>
        </div>
      </div>
      {isExpanded && (
        <div id={contentId} className='request-type-disabled-note__details'>
          {unavailableRequestTypeContent[requestTypeName]}
        </div>
      )}
    </div>
  );
};

const RequestTypes = ({ allowedRequestTypes }) => {
  const { userProfileData } = useUserProfile();

  const { watch, setValue } = useFormContext();
  const requestType = watch('requestType');
  const [expandedKey, setExpandedKey] = useState(null);
  const selectedRequestTypes = [].concat(requestType || []);

  const isRequestTypeDisabled = useCallback(
    (requestTypeName) => allowedRequestTypes?.[requestTypeName] === false,
    [allowedRequestTypes]
  );

  const getRequestTypeAvailability = useCallback(
    (disabledRequestTypes, requestTypeName, selectedRequestTypes) => {
      const isUnavailableForLocation = isRequestTypeDisabled(requestTypeName);
      return {
        isUnavailableForLocation,
        isDisabled: !!_intersect(disabledRequestTypes, selectedRequestTypes)?.length || isUnavailableForLocation,
      };
    },
    [isRequestTypeDisabled]
  );

  const isNprDisabled = useMemo(
    () =>
      getRequestTypeAvailability(
        NprDisabledRequestTypes,
        RequestType.ActivityBasedNoPermitRequired,
        selectedRequestTypes
      ),
    [getRequestTypeAvailability, selectedRequestTypes]
  );

  const isNonPcnDisabled = useMemo(
    () =>
      getRequestTypeAvailability(
        NonPcnDisabledRequestTypes,
        RequestType.NonPreconstructionNotification,
        selectedRequestTypes
      ),
    [getRequestTypeAvailability, selectedRequestTypes]
  );

  const isPermitDisabled = useMemo(
    () =>
      getRequestTypeAvailability(
        PermitApplicationDisabledRequestTypes,
        RequestType.PermitApplication,
        selectedRequestTypes
      ),
    [getRequestTypeAvailability, selectedRequestTypes]
  );

  const isPreAppDisabled = useMemo(
    () => getRequestTypeAvailability(PreAppDisabledRequestTypes, RequestType.PreApplication, selectedRequestTypes),
    [getRequestTypeAvailability, selectedRequestTypes]
  );

  const isJdDisabled = useMemo(
    () =>
      getRequestTypeAvailability(JdDisabledRequestTypes, RequestType.JurisdictionalDetermination, selectedRequestTypes),
    [getRequestTypeAvailability, selectedRequestTypes]
  );

  const disabledByRequestType = useMemo(
    () => ({
      [RequestTypeStringIds.ActivityBasedNoPermitRequired]: isNprDisabled.isDisabled,
      [RequestTypeStringIds.NonPreconstructionNotification]: isNonPcnDisabled.isDisabled,
      [RequestTypeStringIds.AquaticResourceDelineationReport]: isJdDisabled.isDisabled,
      [RequestTypeStringIds.JurisdictionalDetermination]: isJdDisabled.isDisabled,
      [RequestTypeStringIds.PermitApplication]: isPermitDisabled.isDisabled,
      [RequestTypeStringIds.PreApplication]: isPreAppDisabled.isDisabled,
    }),
    [
      isNprDisabled.isDisabled,
      isNonPcnDisabled.isDisabled,
      isJdDisabled.isDisabled,
      isPermitDisabled.isDisabled,
      isPreAppDisabled.isDisabled,
    ]
  );

  // sync form state with availability: remove any selected request types that become disabled
  useEffect(() => {
    const nextRequestTypes = selectedRequestTypes.filter((selectedType) => !disabledByRequestType[selectedType]);
    const hasLocationUpdated =
      nextRequestTypes.length !== selectedRequestTypes.length ||
      nextRequestTypes.some((val, i) => val !== selectedRequestTypes[i]);

    if (!hasLocationUpdated) return;

    setValue('requestType', nextRequestTypes, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [requestType, disabledByRequestType, setValue, selectedRequestTypes]);

  // @TODO WHEN MULTIPLE SELECTIONS ALLOWS, MAKE EACH CHECKBOX NAME UNIQUE
  return (
    <div>
      {userProfileData?.role === ContactTypes.Agent && (
        <Alert className='margin-top-1 margin-bottom-3' type='info'>
          Agents - If you are selecting the "Jurisdictional Determination" option, below, and you have completed a
          delineation and Aquatic Resource Inventory, you must also select the "Submit an Aquatic Resource Delineation
          Report and Aquatic Resource Inventory". Note that you will be prompted to upload the Aquatic Resource
          Inventory at the end of this section.
        </Alert>
      )}

      <h5 className='margin-top-2 margin-bottom-2'>Select a Request Type</h5>

      <div className='margin-left-1'>
        <fieldset className='request-type-fieldset'>
          <legend>Delineation / Jurisdictional Determination</legend>
          <Checkbox
            disabled={isJdDisabled.isDisabled}
            id='1a'
            label={
              <>
                {RequestType.AquaticResourceDelineationReportAndInventory}
                <Tooltip
                  header={RequestType.AquaticResourceDelineationReportAndInventory}
                  name='ar-delineation'
                  content={wotusDelineationReportTooltipContent}
                />
              </>
            }
            name='requestType'
            tile
            value={RequestTypeStringIds.AquaticResourceDelineationReport}
          />
          <Checkbox
            disabled={isJdDisabled.isDisabled}
            id='1b'
            label={
              <>
                {RequestType.JurisdictionalDetermination}
                <Tooltip
                  header={RequestType.JurisdictionalDetermination}
                  name='jurisdictional-determination'
                  content={jurisdictionalDeterminationTooltipContent}
                />
              </>
            }
            name='requestType'
            tile
            value={RequestTypeStringIds.JurisdictionalDetermination}
          />
        </fieldset>

        <fieldset className='request-type-fieldset'>
          <legend>
            Permits & Pre-construction Notifications
            <span className='margin-left-05 text-bold font-sans-3xs'>
              (Use for ALL Permit Types (SP, LOP, NWP, RGP, PGP))   
            </span>
          </legend>
          <Checkbox
            disabled={isPermitDisabled.isDisabled}
            id='permit-application'
            label={
              <>
                Application for Department of the Army (DA) Permit
                <Tooltip
                  header='Application for Department of the Army (DA) Permit'
                  name='permit_application'
                  content={permitTooltipContent}
                />
              </>
            }
            name='requestType'
            tile
            value={RequestTypeStringIds.PermitApplication}
          />
        </fieldset>

        <fieldset className='request-type-fieldset'>
          <legend>Pre-Application / Project Scoping</legend>
          <Checkbox
            disabled={isPreAppDisabled.isDisabled}
            id='pre-app'
            label={
              <>
                Pre-application / Project Scoping
                <Tooltip
                  header='Pre-application / Project Scoping'
                  name='pre-application'
                  content={preAppTooltipContent}
                />
              </>
            }
            name='requestType'
            tile
            value={RequestTypeStringIds.PreApplication}
          />
        </fieldset>
      </div>
      <fieldset className='margin-top-2 margin-bottom-2'>
        <h5>
          Self Screening Tools <span className='text-normal text-italic font-sans-2xs'>(Optional)</span>
        </h5>
        <div className='margin-left-1'>
          <Checkbox
            disabled={isNprDisabled.isDisabled}
            id='npr'
            label={
              <>
                {RequestType.ActivityBasedNoPermitRequired}
                <Tooltip
                  header={RequestType.ActivityBasedNoPermitRequired}
                  name='npr'
                  content={noPermitRequiredTooltipContent}
                />
              </>
            }
            name='requestType'
            tile
            value={RequestTypeStringIds.ActivityBasedNoPermitRequired}
          />
          <Checkbox
            disabled={isNonPcnDisabled.isDisabled}
            id='nonPcn'
            label={
              <>
                <div>
                  {RequestType.NonPreconstructionNotification}
                  <Tooltip
                    header={RequestType.NonPreconstructionNotification}
                    name='nonPcn'
                    content={nonPcnTooltipContent}
                  />
                </div>
                <DisabledRequestTypeNote
                  isDisabled={isNonPcnDisabled.isUnavailableForLocation}
                  requestTypeName={RequestType.NonPreconstructionNotification}
                  expandedKey={expandedKey}
                  setExpandedKey={setExpandedKey}
                />
              </>
            }
            name='requestType'
            tile
            value={RequestTypeStringIds.NonPreconstructionNotification}
          />
        </div>
      </fieldset>
    </div>
  );
};

export default RequestTypes;
